import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, ToggleButtonGroup, ToggleButton,
  Alert, AlertTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Snackbar, Accordion, AccordionSummary, AccordionDetails,
  TextField, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import {
  ExpandMore, Send, Save, Delete, CheckCircle, Error as ErrorIcon,
  People, Visibility, Login as LoginIcon, TrendingDown, Refresh
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  getAnalyticsSettings, saveAnalyticsSettings, deleteAnalyticsSettings,
  testAnalyticsConnection, getAnalyticsOverview, getAnalyticsTimeseries,
  getAnalyticsTopPages, getAnalyticsTrafficSources, getAnalyticsDevices,
  getAnalyticsGeography, sendAnalyticsReport
} from '../services/api';

const COLORS = ['#0350AC', '#0365DD', '#4B92E8', '#8FBCF2', '#2B2E35', '#6C717C'];

function Analytics() {
  const [period, setPeriod] = useState(30);
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [geography, setGeography] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Config state
  const [propertyId, setPropertyId] = useState('');
  const [serviceAccountKey, setServiceAccountKey] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await getAnalyticsSettings();
      setConfigured(res.data);
    } catch {
      setConfigured(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    if (!configured) return;
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        getAnalyticsOverview(period),
        getAnalyticsTimeseries(period),
        getAnalyticsTopPages(period),
        getAnalyticsTrafficSources(period),
        getAnalyticsDevices(period),
        getAnalyticsGeography(period),
      ]);

      if (results[0].status === 'fulfilled') setOverview(results[0].value.data);
      if (results[1].status === 'fulfilled') {
        const data = results[1].value.data.map(d => ({
          ...d,
          date: `${d.date.substring(4, 6)}/${d.date.substring(6, 8)}`
        }));
        setTimeseries(data);
      }
      if (results[2].status === 'fulfilled') setTopPages(results[2].value.data);
      if (results[3].status === 'fulfilled') setTrafficSources(results[3].value.data);
      if (results[4].status === 'fulfilled') setDevices(results[4].value.data);
      if (results[5].status === 'fulfilled') setGeography(results[5].value.data);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [configured, period]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadData(); }, [loadData]);

  const handlePeriodChange = (_, val) => {
    if (val !== null) setPeriod(val);
  };

  const handleTestConnection = async () => {
    if (!propertyId || !serviceAccountKey) {
      showSnackbar('Veuillez remplir tous les champs', 'warning');
      return;
    }
    setConfigLoading(true);
    setTestResult(null);
    try {
      let parsedKey;
      try { parsedKey = JSON.parse(serviceAccountKey); } catch {
        showSnackbar('Le JSON du Service Account est invalide', 'error');
        setConfigLoading(false);
        return;
      }
      const res = await testAnalyticsConnection({ propertyId, serviceAccountKey: parsedKey });
      setTestResult(res.data);
      showSnackbar('Connexion reussie !');
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Echec de connexion', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!propertyId || !serviceAccountKey) {
      showSnackbar('Veuillez remplir tous les champs', 'warning');
      return;
    }
    setConfigLoading(true);
    try {
      let parsedKey;
      try { parsedKey = JSON.parse(serviceAccountKey); } catch {
        showSnackbar('Le JSON du Service Account est invalide', 'error');
        setConfigLoading(false);
        return;
      }
      await saveAnalyticsSettings({ propertyId, serviceAccountKey: parsedKey });
      showSnackbar('Configuration sauvegardee !');
      setPropertyId('');
      setServiceAccountKey('');
      setTestResult(null);
      await loadSettings();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Erreur de sauvegarde', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleDeleteSettings = async () => {
    if (!window.confirm('Supprimer la configuration Google Analytics ?')) return;
    setConfigLoading(true);
    try {
      await deleteAnalyticsSettings();
      showSnackbar('Configuration supprimee');
      setConfigured(false);
      setOverview(null);
      setTimeseries([]);
      setTopPages([]);
      setTrafficSources([]);
      setDevices([]);
      setGeography([]);
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Erreur de suppression', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSendReport = async () => {
    setReportLoading(true);
    try {
      await sendAnalyticsReport(period);
      showSnackbar('Rapport envoye avec succes !');
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Erreur lors de l\'envoi du rapport', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  const kpiCards = overview ? [
    { title: 'Visiteurs', value: overview.activeUsers, icon: <People />, color: COLORS[0] },
    { title: 'Pages vues', value: overview.screenPageViews, icon: <Visibility />, color: COLORS[1] },
    { title: 'Sessions', value: overview.sessions, icon: <LoginIcon />, color: COLORS[2] },
    { title: 'Taux de rebond', value: `${(overview.bounceRate * 100).toFixed(1)}%`, icon: <TrendingDown />, color: COLORS[3] },
  ] : [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">Statistiques</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
            <ToggleButton value={365}>1 an</ToggleButton>
          </ToggleButtonGroup>
          {configured && (
            <Tooltip title="Rafraichir">
              <IconButton onClick={loadData} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Alert if not configured */}
      {configured === false && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Google Analytics non configure</AlertTitle>
          Configurez vos credentials GA4 dans la section ci-dessous pour afficher les statistiques.
        </Alert>
      )}

      {/* Loading */}
      {configured && loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {/* Data sections */}
      {configured && !loading && overview && (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} mb={4}>
            {kpiCards.map((kpi) => (
              <Grid item xs={12} sm={6} md={3} key={kpi.title}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="text.secondary" variant="body2">{kpi.title}</Typography>
                        <Typography variant="h3" fontWeight="bold" sx={{ color: kpi.color }}>
                          {typeof kpi.value === 'number' ? kpi.value.toLocaleString('fr-FR') : kpi.value}
                        </Typography>
                      </Box>
                      <Box sx={{ bgcolor: `${kpi.color}20`, borderRadius: 2, p: 1.5, color: kpi.color }}>
                        {kpi.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Timeseries Chart */}
          {timeseries.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Evolution sur la periode
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={timeseries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis fontSize={12} />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="activeUsers" name="Visiteurs" stroke={COLORS[0]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="screenPageViews" name="Pages vues" stroke={COLORS[1]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS[2]} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Row 2: Top Pages + Traffic Sources */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Pages les plus visitees</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Page</TableCell>
                          <TableCell align="right">Vues</TableCell>
                          <TableCell align="right">Visiteurs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topPages.map((page) => (
                          <TableRow key={page.pagePath}>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {page.pagePath}
                            </TableCell>
                            <TableCell align="right">{page.pageViews.toLocaleString('fr-FR')}</TableCell>
                            <TableCell align="right">{page.activeUsers.toLocaleString('fr-FR')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Sources de trafic</Typography>
                  {trafficSources.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={trafficSources} layout="vertical" margin={{ left: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" fontSize={12} />
                        <YAxis type="category" dataKey="channel" fontSize={12} width={80} />
                        <RechartsTooltip />
                        <Bar dataKey="sessions" name="Sessions" fill={COLORS[0]} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Row 3: Devices + Geography */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Appareils</Typography>
                  {devices.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={devices}
                          dataKey="sessions"
                          nameKey="device"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ device, percent }) => `${device} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {devices.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Geographie</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pays</TableCell>
                          <TableCell align="right">Visiteurs</TableCell>
                          <TableCell align="right">Sessions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {geography.map((geo) => (
                          <TableRow key={geo.country}>
                            <TableCell>{geo.country}</TableCell>
                            <TableCell align="right">{geo.activeUsers.toLocaleString('fr-FR')}</TableCell>
                            <TableCell align="right">{geo.sessions.toLocaleString('fr-FR')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Send Report */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight="bold">Rapport par email</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envoyer un rapport des statistiques actuelles a contact@maladiecameleon.fr
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={reportLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  onClick={handleSendReport}
                  disabled={reportLoading}
                >
                  Envoyer le rapport
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* GA4 Configuration */}
      <Accordion defaultExpanded={!configured}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight="bold">Configuration Google Analytics</Typography>
            {configured && <CheckCircle color="success" fontSize="small" />}
            {configured === false && <ErrorIcon color="warning" fontSize="small" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property ID GA4"
                placeholder="123456789"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                helperText="L'ID numerique de votre propriete GA4"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Service Account JSON"
                placeholder='{"type": "service_account", "project_id": "...", ...}'
                value={serviceAccountKey}
                onChange={(e) => setServiceAccountKey(e.target.value)}
                helperText="Collez le contenu complet du fichier JSON du service account"
              />
            </Grid>
            {testResult && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Connexion reussie ! Utilisateurs actifs aujourd'hui : {testResult.activeUsers}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={configLoading}
                  startIcon={configLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  Tester la connexion
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={configLoading}
                  startIcon={<Save />}
                >
                  Sauvegarder
                </Button>
                {configured && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteSettings}
                    disabled={configLoading}
                    startIcon={<Delete />}
                  >
                    Supprimer la configuration
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Analytics;
